/**
 * Store category visibility helpers for the store front.
 * Mirrors server logic so hidden categories and their listings never appear.
 * Default = visible; only categories with visible === false (or under a hidden parent/group) are excluded.
 */

/**
 * Get the set of visible store category IDs and visible marketplace keys.
 * For store categories: category.visible !== false and, if it has a parent
 * store category, that parent is also visible. For marketplace groups:
 * storeCategoryGroups[mpKey].visible === false means the group is hidden;
 * otherwise it is visible (when configured).
 *
 * @param {Array} storeCategories - Store categories from user profile publicData
 * @param {Object} storeCategoryGroups - Store category groups from user profile publicData
 * @returns {{ visibleCategoryIds: Set<string>, visibleMarketplaceKeys: Set<string> }}
 */
export function getVisibleCategoryIds(storeCategories, storeCategoryGroups) {
  const visibleCategoryIds = new Set();
  const visibleMarketplaceKeys = new Set();
  const categories = Array.isArray(storeCategories) ? storeCategories : [];
  const groups = storeCategoryGroups?.__order ? storeCategoryGroups.__order : [];

  const idToNode = new Map(categories.map(c => [c.id, c]));

  // Marketplace key is visible if group.visible !== false
  groups.forEach(key => {
    if (key.startsWith('mp:') && storeCategoryGroups?.[key]?.visible !== false) {
      visibleMarketplaceKeys.add(key);
    }
  });

  // Helper: parent category ID from parentId string (sc:id -> id)
  const getParentStoreId = (parentId) => {
    if (!parentId || !parentId.startsWith('sc:')) return null;
    return parentId.slice(3);
  };

  // Iteratively add visible categories: top-level first (mp parent and group visible), then children
  let changed = true;
  while (changed) {
    changed = false;
    for (const cat of categories) {
      if (visibleCategoryIds.has(cat.id)) continue;
      if (cat.visible === false) continue;

      const parentId = cat.parentId;
      if (!parentId) {
        visibleCategoryIds.add(cat.id);
        changed = true;
        continue;
      }
      if (parentId.startsWith('mp:')) {
        // Store category under a marketplace: visible if the category itself is visible.
        // When the marketplace group is hidden, the server still writes this category at lvl0 in Algolia, so include it here so it appears on the storefront.
        visibleCategoryIds.add(cat.id);
        changed = true;
        continue;
      }
      const parentStoreId = getParentStoreId(parentId);
      if (parentStoreId && visibleCategoryIds.has(parentStoreId)) {
        visibleCategoryIds.add(cat.id);
        changed = true;
      }
    }
  }

  return { visibleCategoryIds, visibleMarketplaceKeys };
}

/**
 * Check if a category ID or marketplace key is visible (convenience for single id).
 *
 * @param {string} categoryId - Store category ID or marketplace key (e.g. mp:party)
 * @param {Array} storeCategories - Store categories array
 * @param {Object} storeCategoryGroups - Store category groups
 * @returns {boolean}
 */
export function isCategoryIdVisible(categoryId, storeCategories, storeCategoryGroups) {
  if (!categoryId) return false;
  const groups = storeCategoryGroups?.__order ? storeCategoryGroups.__order : [];

  // Marketplace-only mode: no storeCategoryGroups configured → treat all mp keys as visible
  // so that pure marketplace stores can show and link to marketplace categories.
  if (categoryId.startsWith('mp:') && groups.length === 0) {
    return true;
  }

  // When storeCategoryGroups is partial (doesn't contain all mp keys),
  // missing mp keys should default to visible; only explicit visible:false hides.
  if (categoryId.startsWith('mp:')) {
    return storeCategoryGroups?.[categoryId]?.visible !== false;
  }

  const { visibleCategoryIds } = getVisibleCategoryIds(storeCategories, storeCategoryGroups);
  return visibleCategoryIds.has(categoryId);
}

/**
 * Build Algolia filter string so only listings in visible categories (or uncategorized, if allowed) are returned.
 * When visibleIds is empty (all categories hidden), returns a filter that matches no listings.
 *
 * @param {Set<string>|Array<string>} visibleIds - Set or array of visible store category IDs
 * @param {boolean} allowUncategorized - If true, also include listings with no store category
 * @returns {string} Filter string to AND with base filters, or empty string if no category filter needed
 */
export function getVisibleCategoryAlgoliaFilter(visibleIds, allowUncategorized = true) {
  const idSet = visibleIds instanceof Set ? visibleIds : new Set(visibleIds || []);

  if (idSet.size === 0) {
    // All categories hidden: match no listings (use impossible value)
    return 'publicData.storeCategoryId:"__none__"';
  }

  const ids = [...idSet];
  const orParts = [];

  ids.forEach((id) => {
    orParts.push(`publicData.storeCategoryId:"${id}"`);
    orParts.push(`publicData.storeSubCategoryId:"${id}"`);
    orParts.push(`publicData.storeGrandChildCategoryId:"${id}"`);
    orParts.push(`publicData.storeGreatGrandChildCategoryId:"${id}"`);
  });

  // allowUncategorized: Algolia does not support filtering by missing/null attributes.
  // To show uncategorized listings, add a tag at index time (e.g. hasStoreCategory:false) and include it here.

  return orParts.length === 1 ? orParts[0] : `(${orParts.join(' OR ')})`;
}
